import { useParams } from "react-router-dom";

import Container from "@/components/Container";
import Episodes from "@/containers/Episodes";
import MentionCardsProviderOld from "@/containers/MentionCardsProvider";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import TagContext from "@/contexts/TagContext";
import { usePublicReport } from "@/hooks/usePublicReport";
import useSearchParams from "@/hooks/useSearchParams";
import MentionPagination from "@/views/MentionsView/partials/MentionPagination";
import SharedLinkLayout from "@components/SharedLinkLayout";
import Typography from "@components/Typography";

import styles from "./PublicAlertView.module.css";
import MentionCardNew, {
  SkeletonMentionCard,
} from "@components/MentionCardNew";
import { MentionCardsProvider } from "@/containers/MentionCardsContext";

const PublicAlertViewInner = ({ title }: { title: string }) => {
  const { loading, mentionCards, meta } = useMentionCards();

  const [searchParams] = useSearchParams<{
    sentiment: boolean;
    kpis: boolean;
  }>();

  const hasSentiment = searchParams.sentiment;
  const hasKpi = searchParams.kpis;

  return (
    <SharedLinkLayout>
      <header className={styles.header}>
        <Typography className={styles.title} variant="h1">
          {title}
        </Typography>
        {meta?.count ? (
          <Typography
            data-testid="public-report-subtitle"
            className={styles.subtitle}
            variant="subtitle-1"
          >
            {L.PUBLIC_ALERT_SUBTITLE.replaceAll("{}", meta.count)}
          </Typography>
        ) : loading ? null : (
          <Typography className={styles.subtitle} variant="subtitle-1">
            {L.PUBLIC_ALERT_EMPTY_SUBTITLE}
          </Typography>
        )}
      </header>

      <Container>
        <Episodes>
          {loading ? (
            <>
              <SkeletonMentionCard />
              <SkeletonMentionCard />
              <SkeletonMentionCard />
            </>
          ) : (
            mentionCards?.map((mentionCard) => (
              <MentionCardNew
                key={mentionCard.episode.guid}
                hideSentiment={!hasSentiment}
                mentionCard={mentionCard}
                hidePrValue={!hasKpi}
                hideReach={!hasKpi}
                readOnly
              />
            ))
          )}
        </Episodes>

        <MentionPagination
          v4
          disabled={loading}
          className={styles.pagination}
        />
      </Container>
    </SharedLinkLayout>
  );
};

export const PublicAlertView = () => {
  const { uuid } = useParams<{ uuid: Uuid }>();
  const { data: reportData } = usePublicReport(uuid);
  const { title = "" } = reportData?.ok ? reportData.data : {};

  return (
    <TagContext.Provider
      value={{
        organizationTags: [],
        setOrganizationTags: () => undefined,
        fetchOrganizationTags: () => undefined,
      }}
    >
      <MentionCardsProviderOld>
        <MentionCardsProvider>
          <PublicAlertViewInner title={title} />
        </MentionCardsProvider>
      </MentionCardsProviderOld>
    </TagContext.Provider>
  );
};

export const PublicTagView = () => {
  const { id: token } = useParams<{ id: string }>();
  const [data] = atob(token).split(":");
  const jsonString = atob(data);
  const { org_uuid: orgUuid, tag } = JSON.parse(jsonString);

  return (
    <TagContext.Provider
      value={{
        organizationTags: [],
        setOrganizationTags: () => undefined,
        fetchOrganizationTags: () => undefined,
      }}
    >
      <MentionCardsProviderOld overrideSlug={tag} orgUuid={orgUuid}>
        <MentionCardsProvider>
          <PublicAlertViewInner title={tag} />
        </MentionCardsProvider>
      </MentionCardsProviderOld>
    </TagContext.Provider>
  );
};
